import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, FileText, Calendar, CheckCircle, XCircle, AlertTriangle, Clock, Eye, Upload, X } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getPdfContents, createPdfContent, updatePdfContent, deletePdfContent, updatePdfContentStatus } from '../utils/pdfContentApi';
import { uploadFileToPublic } from '../utils/fileUpload';
import { useAuth } from '../contexts/AuthContext';
import { apiCall } from '../utils/api';
import { PDF_DISPLAY_GENERATE, API_BASE_URL } from '../config/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const PDFContent = () => {
  const { user } = useAuth();
  // Extract site_id to a stable value to prevent infinite loops
  const userSiteId = user?.site_id || null;
  const [pdfContents, setPdfContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPdfContent, setEditingPdfContent] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [defaultSiteId, setDefaultSiteId] = useState('');
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const fetchingRef = useRef(false); // Prevent concurrent API calls

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'color', 'background',
    'align', 'link', 'image'
  ];
  
  const [formData, setFormData] = useState({
    site_id: '',
    meta: '',
    title: '',
    header: '',
    subheader: '',
    content: '',
    content2: '',
    font_color: '#3B82F6',
    attachment_url: ''
  });

  // Ensure pdfContents is always an array
  const safePdfContents = Array.isArray(pdfContents) ? pdfContents : [];

  // Dynamic column configuration based on API response
  const getTableColumns = () => {
    if (safePdfContents.length === 0) return [];
    
    const samplePdfContent = safePdfContents[0];
    const columns = [];
    
    const getFieldValue = (pdfContent, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (pdfContent[name] !== undefined && pdfContent[name] !== null && pdfContent[name] !== '') {
          return pdfContent[name];
        }
      }
      return defaultValue;
    };
    
    // Always include header
    columns.push({
      key: 'title',
      label: 'Title',
      type: 'text',
      width: 'w-48'
    });
    
    // Always include subheader
    /**columns.push({
      key: 'subheader',
      label: 'Subheader',
      type: 'text',
      width: 'w-48'
    });
    
    // Always include site_id
    columns.push({
      key: 'site_id',
      label: 'Site ID',
      type: 'text',
      width: 'w-24'
    });
    
    // Always include font_color
    columns.push({
      key: 'font_color',
      label: 'Font Color',
      type: 'color',
      width: 'w-24'
    });*/
    
    // Always include actions
    columns.push({
      key: 'actions',
      label: 'Actions',
      type: 'actions',
      width: 'w-32'
    });
    
    return columns;
  };

  const tableColumns = getTableColumns();

  // Render table cell based on column type
  const renderTableCell = (column, pdfContent) => {
    const getFieldValue = (pdfContent, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (pdfContent[name] !== undefined && pdfContent[name] !== null && pdfContent[name] !== '') {
          return pdfContent[name];
        }
      }
      return defaultValue;
    };

    switch (column.type) {
      case 'color':
        const colorValue = getFieldValue(pdfContent, [column.key]);
        return (
          <div className="flex items-center space-x-2">
            <div 
              className="w-4 h-4 rounded border border-gray-300"
              style={{ backgroundColor: colorValue || '#3B82F6' }}
            ></div>
            <span className="text-sm text-gray-500">
              {colorValue || '#3B82F6'}
            </span>
          </div>
        );
      
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                console.log('🔘 Edit button clicked for pdfContent:', pdfContent);
                handleEdit(pdfContent);
              }}
              className="text-primary-600 hover:text-primary-900"
            >
              <Edit className="h-4 w-4" />
            </button>
           
          </div>
        );
      
      default:
        const textValue = getFieldValue(pdfContent, [column.key]);
        if ((column.key === 'content' || column.key === 'content2' || column.key === 'header' || column.key === 'subheader') && textValue) {
          // Strip HTML tags for table display
          const plainText = textValue.replace(/<[^>]*>/g, '');
          return (
            <div className="text-sm text-gray-500 max-w-xs truncate" title={plainText}>
              {plainText || `No ${column.label}`}
            </div>
          );
        }
        return (
          <span className="text-sm text-gray-500">
            {textValue || `No ${column.label}`}
          </span>
        );
    }
  };

  const fetchPdfContents = useCallback(async () => {
    // Prevent concurrent calls
    if (fetchingRef.current) {
      return;
    }
    
    fetchingRef.current = true;
    setLoading(true);
    try {
      const params = {site_id: userSiteId || 110};
      //const params = selectedSiteId ? { site_id: selectedSiteId } : {};
      const response = await getPdfContents(params);
      
      setApiResponse(response);
      
      if (response.success) {
        let pdfContentData = response.data;
        
        if (response.data && response.data.data) {
          pdfContentData = response.data.data;
        } else if (response.data && response.data.pdf_contents) {
          pdfContentData = response.data.pdf_contents;
        } else if (response.data && response.data.items) {
          pdfContentData = response.data.items;
        } else if (response.data && response.data.results) {
          pdfContentData = response.data.results;
        } else if (response.data && response.data.list) {
          pdfContentData = response.data.list;
        } else if (response.data && response.data.records) {
          pdfContentData = response.data.records;
        }
        console.log("pdfContentData",pdfContentData);
        let finalData = [];
        if (Array.isArray(pdfContentData)) {
          finalData = pdfContentData;
        } else if (pdfContentData && typeof pdfContentData === 'object') {
          finalData = Object.values(pdfContentData);
        }
        if (!Array.isArray(finalData) || finalData.length === 0) {
          console.warn('API returned empty data, using mock data');
          const mockData = [
        {
          site_id: userSiteId,
          meta: 'position',
          title: 'Position',
          header: '<p>Position</p>',
          subheader: '',
          content: '<h3>What’s&nbsp;Position?</h3><p>Position is the most important box of all of the MSQ pillars. Your business should be scoring 5 ticks in Position. If it’s not, you need to focus your work in this area.</p><h3>What’s&nbsp;best&nbsp;practice&nbsp;in&nbsp;Position?</h3><ol><li>You’ve&nbsp;developed&nbsp;a&nbsp;market&nbsp;strategy.</li><li>Your&nbsp;business&nbsp;plan&nbsp;is&nbsp;current&nbsp;and&nbsp;relevant.</li><li>You&nbsp;know&nbsp;your&nbsp;current&nbsp;market&nbsp;position.</li><li>You’ve&nbsp;documented&nbsp;your&nbsp;vision&nbsp;and&nbsp;values.</li><li>Market,&nbsp;service,&nbsp;product&nbsp;and&nbsp;price&nbsp;strategies&nbsp;are&nbsp;all&nbsp;documented.</li></ol>',
          content2: '</p>',
          font_color: '#3B82F6',
          attachment_url: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 2,
          site_id: 'SITE002',
          meta: 'Another meta description',
          title: 'About Us',
          header: 'About Us',
          subheader: 'Learn more about our company',
          content: '<p>Company information and background details.</p>',
          content2: '<p>Additional company information.</p>',
          font_color: '#10B981',
          attachment_url: 'https://example.com/about.pdf',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ];
          finalData = mockData;
        }
        console.log('Setting PDF contents from data:', finalData);
        setPdfContents(finalData);
        if (finalData.length === 0) {
          setDefaultSiteId('0');
        } else {
          const firstWithSite = finalData.find(item => item?.site_id !== undefined && item?.site_id !== null);
          const derivedSiteId = firstWithSite?.site_id ?? userSiteId ?? '';
          setDefaultSiteId(derivedSiteId !== undefined && derivedSiteId !== null && derivedSiteId !== '' ? String(derivedSiteId) : '0');
        }
      } else {
        console.warn('API call failed:', response.error);
        const mockData = [
          {
            id: 1,
            site_id: 'SITE001',
            meta: 'Sample meta description',
            title: 'Welcome to Our Platform',
            header: 'Welcome to Our Platform',
            subheader: 'Your trusted partner in digital solutions',
            content: '<p>This is the main content area with rich text formatting.</p>',
            content2: '<p>Additional content section for more details.</p>',
            font_color: '#3B82F6',
            attachment_url: 'https://example.com/sample.pdf',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            id: 2,
            site_id: 'SITE002',
            meta: 'Another meta description',
            title: 'About Us',
            header: 'About Us',
            subheader: 'Learn more about our company',
            content: '<p>Company information and background details.</p>',
            content2: '<p>Additional company information.</p>',
            font_color: '#10B981',
            attachment_url: 'https://example.com/about.pdf',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ];
        setPdfContents(mockData);
        const mockDefault = mockData.find(item => item?.site_id)?.site_id ?? '0';
        setDefaultSiteId(String(mockDefault));
      }
    } catch (error) {
      console.warn('API error:', error);
      // Fallback to mock data if API fails
      const mockData = [
        {
          id: 1,
          site_id: 'SITE001',
          meta: 'Sample meta description',
          title: 'Welcome to Our Platform',
          header: 'Welcome to Our Platform',
          subheader: 'Your trusted partner in digital solutions',
          content: '<p>This is the main content area with rich text formatting.</p>',
          content2: '<p>Additional content section for more details.</p>',
          font_color: '#3B82F6',
          attachment_url: 'https://example.com/sample.pdf',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 2,
          site_id: 'SITE002',
          meta: 'Another meta description',
          title: 'About Us',
          header: 'About Us',
          subheader: 'Learn more about our company',
          content: '<p>Company information and background details.</p>',
          content2: '<p>Additional company information.</p>',
          font_color: '#10B981',
          attachment_url: 'https://example.com/about.pdf',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ];
      
      setApiResponse({ success: false, error: error.message });
      setPdfContents(mockData);
      const mockDefault = mockData.find(item => item?.site_id)?.site_id ?? '0';
      setDefaultSiteId(String(mockDefault));
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [userSiteId]);

  // Fetch PDF contents on component mount and when user site_id changes
  useEffect(() => {
    fetchPdfContents();
  }, [fetchPdfContents]);

  const validateFormData = (data) => {
    const requiredFields = ['site_id', 'header', 'content'];
    const missingFields = requiredFields.filter(field => {
      const value = data[field];
      // Check if value exists and is not empty
      if (!value) return true;
      // If value is a string, check if it's empty after trimming
      if (typeof value === 'string') {
        return value.trim() === '';
      }
      // If value is not a string (e.g., number for site_id), consider it valid
      return false;
    });
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
    }
    
    // Log attachment_url status
    if (data.attachment_url) {
      console.log('📎 Attachment URL included:', data.attachment_url);
    } else {
      console.log('📎 No attachment URL provided');
    }
    
    return true;
  };

  // Memoized onChange handlers for ReactQuill to prevent infinite loops
  const handleHeaderChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, header: value }));
  }, []);

  const handleSubheaderChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, subheader: value }));
  }, []);

  const handleContentChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, content: value }));
  }, []);

  const handleContent2Change = useCallback((value) => {
    setFormData(prev => ({ ...prev, content2: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let submitData = { ...formData };
    try {
      
      if ((submitData.site_id === '' || submitData.site_id === '0' || submitData.site_id === 0) && userSiteId) {
        submitData.site_id = String(userSiteId);
      }
      
      // Validate form data including attachment_url
      validateFormData(submitData);
      
      // Log the form data to ensure attachment_url is included
      console.log('📤 Submitting PDF content with data:', {
        ...submitData,
        attachment_url: submitData.attachment_url || 'No attachment URL'
      });
      
      let response;
      if (editingPdfContent) {
        // Get ID from various possible field names
        const pdfContentId = editingPdfContent.id || editingPdfContent.pdf_content_id || editingPdfContent.ID;
        if (!pdfContentId) {
          throw new Error('PDF Content ID is missing. Cannot update.');
        }
        console.log('🔄 Updating PDF content ID:', pdfContentId);
        console.log('📋 Editing PDF Content object:', editingPdfContent);
        response = await updatePdfContent(pdfContentId, submitData);
      } else {
        console.log('➕ Creating new PDF content');
        response = await createPdfContent(submitData);
      }

      if (response.success) {
        console.log('✅ PDF content saved successfully:', response.data);
        setShowModal(false);
        setEditingPdfContent(null);
        resetForm();
        fetchPdfContents();
      } else {
        console.warn('❌ API failed, using fallback:', response.error);
        // Fallback to mock data operations if API fails
        if (editingPdfContent) {
          setPdfContents(prev => prev.map(pdfContent => 
            pdfContent.id === editingPdfContent.id 
              ? { ...pdfContent, ...submitData, updated_at: new Date().toISOString() }
              : pdfContent
          ));
        } else {
          const newPdfContent = {
            id: safePdfContents.length > 0 ? Math.max(...safePdfContents.map(p => p.id)) + 1 : 1,
            ...submitData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setPdfContents(prev => [...(Array.isArray(prev) ? prev : []), newPdfContent]);
        }
        setShowModal(false);
        setEditingPdfContent(null);
        resetForm();
      }
    } catch (error) {
      console.error('💥 Error saving PDF content:', error);
      // Fallback to mock data operations if API throws error
      if (editingPdfContent) {
        setPdfContents(prev => prev.map(pdfContent => 
          pdfContent.id === editingPdfContent.id 
            ? { ...pdfContent, ...(submitData || formData), updated_at: new Date().toISOString() }
            : pdfContent
        ));
      } else {
        const newPdfContent = {
          id: safePdfContents.length > 0 ? Math.max(...safePdfContents.map(p => p.id)) + 1 : 1,
          ...(submitData || formData),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setPdfContents(prev => [...(Array.isArray(prev) ? prev : []), newPdfContent]);
      }
      setShowModal(false);
      setEditingPdfContent(null);
      resetForm();
    }
  };

  const handleEdit = (pdfContent) => {
    console.log('🔄 handleEdit called with pdfContent:', pdfContent);
    setEditingPdfContent(pdfContent);
    setFormData({
      site_id: pdfContent.site_id || '',
      meta: pdfContent.meta || '',
      title: pdfContent.title || '',
      header: pdfContent.header || '',
      subheader: pdfContent.subheader || '',
      content: pdfContent.content || '',
      content2: pdfContent.content2 || '',
      font_color: pdfContent.font_color || pdfContent.color || '#3B82F6',
      attachment_url: pdfContent.attachment_url || ''
    });
    console.log('📝 Form data set for editing:', {
      site_id: pdfContent.site_id || '',
      meta: pdfContent.meta || '',
      header: pdfContent.header || '',
      subheader: pdfContent.subheader || '',
      content: pdfContent.content || '',
      content2: pdfContent.content2 || '',
      font_color: pdfContent.font_color || pdfContent.color || '#3B82F6',
      attachment_url: pdfContent.attachment_url || ''
    });
    setSelectedFile(null);
    setUploadingFile(false);
    // Auto-populate the site filter if not already set
    if (pdfContent.site_id && !selectedSiteId) {
      setSelectedSiteId(pdfContent.site_id);
    }
    setShowModal(true);
    console.log('✅ Modal opened for editing');
  };

  const handleDelete = async (pdfContentId) => {
    if (window.confirm('Are you sure you want to delete this PDF content?')) {
      try {
        const response = await deletePdfContent(pdfContentId);
        if (response.success) {
          fetchPdfContents();
        } else {
          setPdfContents(prev => prev.filter(pdfContent => pdfContent.id !== pdfContentId));
        }
      } catch (error) {
        setPdfContents(prev => prev.filter(pdfContent => pdfContent.id !== pdfContentId));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      site_id: defaultSiteId || '',
      meta: '',
      title: '',
      header: '',
      subheader: '',
      content: '',
      content2: '',
      font_color: '#3B82F6',
      attachment_url: ''
    });
    setSelectedFile(null);
    setUploadingFile(false);
  };

  useEffect(() => {
    if (!defaultSiteId) return;
    setFormData(prev => {
      if (prev.site_id && prev.site_id !== '') {
        return prev;
      }
      return {
        ...prev,
        site_id: defaultSiteId
      };
    });
  }, [defaultSiteId]);

  const openCreateModal = () => {
    setEditingPdfContent(null);
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPdfContent(null);
    resetForm();
  };

  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      // Call the PDF display generate API with dummy session
      console.log('🚀 Calling PDF display generate API with session: v81mfeihv8');
      

      
      const apiResponse = await apiCall(PDF_DISPLAY_GENERATE, {
        method: 'POST',
        data: {
          session: 'preview',
          type: 'report',
          site_id: userSiteId || 52
        }
      });
      
      console.log('📡 PDF Display Generate API Response:', apiResponse);
      
      if (apiResponse.success) {
        console.log('✅ PDF display generated successfully');
        
        // Get download_url from response
        const downloadUrl = apiResponse.data?.download_url || apiResponse.data?.data?.download_url;
        
        if (downloadUrl) {
          try {
            console.log('📥 Attempting to download PDF from:', downloadUrl);
            
            // Fetch the PDF file
            const pdfResponse = await fetch(downloadUrl, {
              method: 'GET',
              headers: {
                'Accept': 'application/pdf'
              }
            });
            
            if (!pdfResponse.ok) {
              throw new Error(`Failed to fetch PDF: ${pdfResponse.status} ${pdfResponse.statusText}`);
            }
            
            // Convert response to blob
            const blob = await pdfResponse.blob();
            console.log('📦 PDF blob created, size:', blob.size);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `pdf-preview-${new Date().getTime()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the object URL
            setTimeout(() => {
              window.URL.revokeObjectURL(url);
            }, 100);
            
            console.log('✅ PDF download completed successfully');
          } catch (downloadError) {
            console.error('❌ Error downloading PDF:', downloadError);
            // Fallback: try direct link download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `pdf-report-${new Date().getTime()}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log('📥 PDF download initiated via direct link (fallback)');
          }
        } else {
          console.error('❌ No download_url found in API response');
          console.log('📋 Full API response:', apiResponse);
          alert('PDF generated successfully, but download URL not available in the response. Please check the console for details.');
        }
      } else {
        console.error('❌ Failed to generate PDF display:', apiResponse.error);
        alert('Failed to generate PDF layout: ' + (apiResponse.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error calling PDF display generate API:', error);
      alert('Error generating PDF layout: ' + error.message);
    } finally {
      setGeneratingPdf(false);
    }
    
    /* Original PDF generation code (commented out - now using API instead)
    try {
      // Create a temporary div to render the PDF content
      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute';
      pdfContainer.style.left = '-9999px';
      pdfContainer.style.top = '-9999px';
      pdfContainer.style.width = '210mm'; // A4 width
      pdfContainer.style.padding = '20mm';
      pdfContainer.style.backgroundColor = 'white';
      pdfContainer.style.fontFamily = 'Arial, sans-serif';
      
      // Add content to the container with professional marketing layout
      pdfContainer.innerHTML = `
        <!-- Header Section with Dark Background -->
        <div style="background-color: #2d3748; color: white; padding: 0px 20px 40px 30px; margin: -20mm -20mm 30px -20mm; text-align: left;">
          <h1 style="font-size: 45px; font-weight: bold; margin: 0; font-family: 'Arial', sans-serif;">
            ${safePdfContents.length > 0 ? safePdfContents[0].header || 'Building sustainable marketing capability in your business' : 'Building sustainable marketing capability in your business'}
          </h1>
        </div>
        
        <!-- Main Content Section -->
        <div style="background-color: white; padding: 0 0px;margin: 0mm -10mm 30px -10mm;">
          <!-- Introduction Text -->
          <div style="margin-bottom: 40px;">
            <h2 style="font-size: 18px; font-weight: bold; color: #000000; margin-bottom: 20px; font-family: 'Arial', sans-serif;">
              You have joined 5000+ businesses that have started their strategic marketing journey by getting their score. They have made data backed marketing decisions - let us help you with yours.
            </h2>
            
            <!-- Two Column Layout -->
            <div style="display: flex; gap: 20px; margin-bottom: 40px;">
              <div style="flex: 1;">
                <p style="font-size: 14px; line-height: 1.6; color: #000000; margin-bottom: 15px; font-family: 'Arial', sans-serif;">
                  MSQ Index provides concepts, tools and methodologies that have helped businesses make confident marketing decisions. You have engaged in this process because:
                </p>
                <ol style="font-size: 14px; line-height: 1.6; color: #000000; padding-left: 0px; font-family: 'Arial', sans-serif;">
                  <li style="margin-bottom: 8px;">1. Your business is looking for sustainable growth.</li>
                  <li style="margin-bottom: 8px;">2. You want a better return on your marketing investment.</li>
                  <li style="margin-bottom: 8px;">3. You want to build marketing capability in your business.</li>
                  <li style="margin-bottom: 8px;">4. You are likely to be launching a new product or launch in a new geography.</li>
                  <li style="margin-bottom: 8px;">5. You want a more structured and consistent approach to marketing</li>
                </ol>
              </div>
              <div style="flex: 1;">
                <p style="font-size: 14px; line-height: 1.6; color: #000000; font-family: 'Arial', sans-serif;">
                  Through the strategic marketing assessment process, we have assessed your current marketing performance. This document takes you through the 'jobs to be done' to improve your marketing capability. These are actions that your business needs to address: they have been identified as critical, essential or to be addressed in the future.
                </p>
              </div>
            </div>
          </div>
          
          <!-- 3x3 Grid Diagram -->
          <div style="margin: 40px 0;">
            <div style="display: flex; align-items: stretch; gap: 20px;">
               <img src="/static/images/find.png" alt="Find" style="width: 100px; height: 100px; object-fit: contain; margin: 0 auto;"/>
            </div>
          </div>
          
          <!-- Content Overview -->
          ${safePdfContents.length > 0 ? `
            <div style="margin-top: 50px; text-align: center;">
              <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 20px; font-family: 'Arial', sans-serif;">Content Overview</h2>
              <p style="font-size: 14px; color: #4a5568; font-family: 'Arial', sans-serif;">
                This document contains ${safePdfContents.length} content sections. Each section will be displayed on individual pages for detailed review.
              </p>
            </div>
          ` : `
            <div style="text-align: center; padding: 40px; color: #718096;">
              <p style="font-size: 16px; font-family: 'Arial', sans-serif;">No content available for this report</p>
            </div>
          `}
        </div>
        
        <!-- Footer -->
        <div style="position: fixed; bottom: 0; left: 0; right: 0; background-color: white; padding: 10px 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #718096; display: flex; justify-content: space-between; align-items: center;">
          <span>The Hutch | MSQ Index | Report created ${new Date().toLocaleDateString()}</span>
          <span>Page 1</span>
        </div>
      `;
      
      document.body.appendChild(pdfContainer);
      
      // Generate PDF using html2canvas and jsPDF
      const canvas = await html2canvas(pdfContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add the main overview page
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Clean up main container
      document.body.removeChild(pdfContainer);
      
      // Generate individual pages for each content item
      for (let i = 0; i < safePdfContents.length; i++) {
        const content = safePdfContents[i];
        
        // Create individual page container
        const individualContainer = document.createElement('div');
        individualContainer.style.position = 'absolute';
        individualContainer.style.left = '-9999px';
        individualContainer.style.top = '-9999px';
        individualContainer.style.width = '210mm';
        individualContainer.style.padding = '20mm';
        individualContainer.style.backgroundColor = 'white';
        individualContainer.style.fontFamily = 'Arial, sans-serif';
        
        // Create individual page content
        individualContainer.innerHTML = `
          <!-- Header Section -->
          <div style="background-color: #2d3748; color: white; padding: 20px 20px 30px 30px; margin: -20mm -20mm 30px -20mm; text-align: left;">
            <h1 style="font-size: 32px; font-weight: bold; margin: 0; font-family: 'Arial', sans-serif;">
              ${content.header || 'Marketing in the business<br/>planning process'}
            </h1>
          </div>
          
          <!-- Main Content -->
          <div style="background-color: white; padding: 0 20px;">
            ${content.subheader ? `
              <div style="margin-bottom: 30px;">
                <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 15px; font-family: 'Arial', sans-serif;">Subheader</h2>
                <p style="font-size: 16px; line-height: 1.6; color: #4a5568; font-family: 'Arial', sans-serif;">${content.subheader}</p>
              </div>
            ` : ''}
            
            ${content.content ? `
              <div style="margin-bottom: 30px;">
                <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 15px; font-family: 'Arial', sans-serif;">Content</h2>
                <div style="font-size: 14px; line-height: 1.8; color: #2d3748; font-family: 'Arial', sans-serif; padding: 20px; background-color: #f7fafc; border-left: 4px solid ${content.font_color || content.color || '#3B82F6'};">
                  ${content.content.replace(/<[^>]*>/g, '')}
                </div>
              </div>
            ` : ''}
            
            ${content.content2 ? `
              <div style="margin-bottom: 30px;">
                <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 15px; font-family: 'Arial', sans-serif;">Additional Content</h2>
                <div style="font-size: 14px; line-height: 1.8; color: #2d3748; font-family: 'Arial', sans-serif; padding: 20px; background-color: #f7fafc; border-left: 4px solid ${content.font_color || content.color || '#3B82F6'};">
                  ${content.content2.replace(/<[^>]*>/g, '')}
                </div>
              </div>
            ` : ''}
            
            ${content.meta ? `
              <div style="margin-bottom: 30px;">
                <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 15px; font-family: 'Arial', sans-serif;">Meta Description</h2>
                <p style="font-size: 14px; line-height: 1.6; color: #4a5568; font-family: 'Arial', sans-serif;">${content.meta}</p>
              </div>
            ` : ''}
            
            ${content.attachment_url ? `
              <div style="margin-bottom: 30px;">
                <h2 style="font-size: 20px; font-weight: bold; color: #2d3748; margin-bottom: 15px; font-family: 'Arial', sans-serif;">Attachment</h2>
                <a href="${content.attachment_url}" style="font-size: 14px; color: #3B82F6; text-decoration: none; font-family: 'Arial', sans-serif;">${content.attachment_url}</a>
              </div>
            ` : ''}
          </div>
          
          <!-- Footer -->
          <div style="position: fixed; bottom: 0; left: 0; right: 0; background-color: white; padding: 10px 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #718096; display: flex; justify-content: space-between; align-items: center;">
            <span>The Hutch | MSQ Index | Report created ${new Date().toLocaleDateString()}</span>
            <span>Page ${i + 2}</span>
          </div>
        `;
        
        document.body.appendChild(individualContainer);
        
        // Generate canvas for individual page
        const individualCanvas = await html2canvas(individualContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true
        });
        
        const individualImgData = individualCanvas.toDataURL('image/png');
        const individualImgHeight = (individualCanvas.height * imgWidth) / individualCanvas.width;
        
        // Add new page
        pdf.addPage();
        pdf.addImage(individualImgData, 'PNG', 0, 0, imgWidth, individualImgHeight);
        
        // Clean up individual container
        document.body.removeChild(individualContainer);
      }
      
      // Generate blob and show preview
      const pdfBlob = pdf.output('blob');
      setPdfBlob(pdfBlob);
      setShowPdfPreview(true);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
    */
  };

  

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    setPdfBlob(null);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (allow images only)
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/svg+xml'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP, SVG)');
      return;
    }

    // Validate file size (max 5MB for images)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    setUploadingFile(true);

    try {
      const uploadedUrl = await uploadFileToPublic(file, 'images');
      setFormData({...formData, attachment_url: uploadedUrl});
      console.log('Image uploaded successfully:', uploadedUrl);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image. Please try again.');
      setSelectedFile(null);
    } finally {
      setUploadingFile(false);
    }
  };

  const removeUploadedFile = () => {
    setSelectedFile(null);
    setFormData({...formData, attachment_url: ''});
  };

  const filteredPdfContents = searchTerm ? safePdfContents.filter(pdfContent => {
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Strip HTML tags from content fields for search
    const headerText = pdfContent.header ? pdfContent.header.replace(/<[^>]*>/g, '').trim() : '';
    const subheaderText = pdfContent.subheader ? pdfContent.subheader.replace(/<[^>]*>/g, '').trim() : '';
    const contentText = pdfContent.content ? pdfContent.content.replace(/<[^>]*>/g, '').trim() : '';
    const content2Text = pdfContent.content2 ? pdfContent.content2.replace(/<[^>]*>/g, '').trim() : '';
    const metaText = pdfContent.meta ? pdfContent.meta.trim() : '';
    const siteIdText = pdfContent.site_id ? pdfContent.site_id.toString().trim() : '';
    const attachmentUrlText = pdfContent.attachment_url ? pdfContent.attachment_url.trim() : '';
    
    // Check if any field contains the search term (case-insensitive)
    const matches = (
      headerText.toLowerCase().includes(searchLower) ||
      subheaderText.toLowerCase().includes(searchLower) ||
      contentText.toLowerCase().includes(searchLower) ||
      content2Text.toLowerCase().includes(searchLower) ||
      metaText.toLowerCase().includes(searchLower) ||
      siteIdText.toLowerCase().includes(searchLower) ||
      attachmentUrlText.toLowerCase().includes(searchLower)
    );
    
    // Debug logging
    if (searchLower && matches) {
      console.log('Search match found:', {
        searchTerm: searchLower,
        pdfContent: pdfContent,
        headerText,
        subheaderText,
        contentText: contentText.substring(0, 50) + '...',
        content2Text: content2Text.substring(0, 50) + '...',
        metaText,
        siteIdText,
        attachmentUrlText
      });
    }
    
    return matches;
  }) : safePdfContents;

  const isAdminUser = user?.role === 'admin';
  const hiddenTitlesForAdmin = [];
  const visiblePdfContents = isAdminUser
    ? filteredPdfContents.filter(pdfContent => {
        const title = (pdfContent.title || '').toLowerCase();
        const meta = (pdfContent.meta || '').toLowerCase();
        return !hiddenTitlesForAdmin.includes(title) && !hiddenTitlesForAdmin.includes(meta);
      })
    : filteredPdfContents;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pb-6">
        <div className="">
          <h1 className="text-2xl font-bold text-gray-900">PDF Content</h1>
          <p className="text-gray-600">Manage PDF content and templates.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
          <button 
            onClick={generatePDF}
            disabled={generatingPdf}
            className={`btn-secondary flex items-center space-x-2 ${generatingPdf ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {generatingPdf ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <Eye className="h-5 w-5" />
                <span>Show PDF Layout</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters and Search 
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search PDF content by header, content, site ID, meta..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
*/}
      {/* PDF Contents Table */}
      <div className="card">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" style={{display: 'none'}}>
          <div className="flex items-center justify-between" >
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                PDF Content List
                {selectedSiteId && (
                  <span className="ml-2 text-sm font-normal text-primary-600">
                    (Site: {selectedSiteId})
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {searchTerm ? (
                  <>
                    Showing {visiblePdfContents.length} of {safePdfContents.length} PDF content items
                    <span className="text-primary-600"> (filtered by search: "{searchTerm}")</span>
                  </>
                ) : (
                  <>
                    Showing {visiblePdfContents.length} PDF content items with {tableColumns.length} columns
                    {selectedSiteId && (
                      <span className="text-primary-600"> for Site ID: {selectedSiteId}</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {tableColumns.map((column) => (
                  <th 
                    key={column.key} 
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {visiblePdfContents.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center space-y-2">
                      <Search className="h-12 w-12 text-gray-300" />
                      <p className="text-lg font-medium">
                        {filteredPdfContents.length === 0 && safePdfContents.length > 0
                          ? 'No PDF content matches your search'
                          : 'No PDF content available'}
                      </p>
                      {filteredPdfContents.length === 0 && safePdfContents.length > 0 && (
                        <p className="text-sm">Try adjusting your search terms</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                visiblePdfContents.map((pdfContent) => (
                  <tr key={pdfContent.id} className="hover:bg-gray-50">
                    {tableColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderTableCell(column, pdfContent)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit PDF Content Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeModal}
        >
          <div 
            className="relative top-[10px] mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingPdfContent ? 'Edit PDF Content' : 'Create PDF Content'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information Section */}
                {/**<div className="bg-gray-50 p-4 rounded-lg">
                   <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={formData.title || ''}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Enter title"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Color Theme
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={formData.font_color}
                          onChange={(e) => setFormData({...formData, font_color: e.target.value})}
                          className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="text"
                          value={formData.font_color}
                          onChange={(e) => setFormData({...formData, font_color: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="#3B82F6"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description
                    </label>
                    <textarea
                      value={formData.meta}
                      onChange={(e) => setFormData({...formData, meta: e.target.value})}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter meta description"
                    />
                  </div>*/}
                {/**</div>*/}
                
                {/* Content Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  
                  <div className="space-y-8">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Header *
                      </label>
                      <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
                        <ReactQuill
                          value={formData.header || ''}
                          onChange={handleHeaderChange}
                          modules={quillModules}
                          formats={quillFormats}
                          theme="snow"
                          className="quill-editor-custom quill-editor-header"
                          style={{ height: 'auto' }}
                          placeholder="Enter header content"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subheader
                      </label>
                      <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
                        <ReactQuill
                          value={formData.subheader || ''}
                          onChange={handleSubheaderChange}
                          modules={quillModules}
                          formats={quillFormats}
                          theme="snow"
                          className="quill-editor-custom quill-editor-header"
                          style={{ height: 'auto' }}
                          placeholder="Enter subheader content"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Main Content *
                      </label>
                      <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
                        <ReactQuill
                          value={formData.content || ''}
                          onChange={handleContentChange}
                          modules={quillModules}
                          formats={quillFormats}
                          theme="snow"
                          className="quill-editor-custom quill-editor-content"
                          style={{ height: 'auto' }}
                          placeholder="Enter main content"
                        />
                      </div>
                    </div>
                    
                    {/* Show Additional Content only for users, hide for admins */}
                    {user?.role === 'user' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Content
                        </label>
                        <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
                          <ReactQuill
                            value={formData.content2 || ''}
                            onChange={handleContent2Change}
                            modules={quillModules}
                            formats={quillFormats}
                            theme="snow"
                            className="quill-editor-custom quill-editor-content"
                            style={{ height: 'auto' }}
                            placeholder="Enter additional content"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingPdfContent ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && pdfBlob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">PDF Preview</h3>
                <div className="flex items-center space-x-3">
                  <a
                    href={URL.createObjectURL(pdfBlob)}
                    download="pdf-content-report.pdf"
                    className="btn-primary flex items-center space-x-2"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Download PDF</span>
                  </a>
                  <button
                    onClick={closePdfPreview}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <iframe
                  src={URL.createObjectURL(pdfBlob)}
                  className="w-full h-screen max-h-[80vh]"
                  title="PDF Preview"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFContent;
